import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { Component, Input, ViewEncapsulation, ViewChild, AfterViewInit, Output, EventEmitter, ElementRef } from '@angular/core';
import { MatStepper } from '@angular/material/stepper';
import { DateTime as LuxonDateTime } from 'luxon';
import { ParseDateTimePipe } from 'src/shared/pipes/parse-date.pipe';

@Component({
    selector: 'tidy-stepper',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./stepper.component.scss'],
    template: `
  <mat-stepper
    #stepper
    class="tidy-stepper"
    orientation="vertical"
    [linear]="false"
    (selectionChange)="stepChange($event)"
    (click)="collapseAll(this.stepsRefs, false); focusUnfocusStep(stepper.selectedIndex)"
  
    inViewport
    [inViewportOptions]="{ threshold: [0] }"
    (inViewportAction)="collapseAll(stepsRefs)"
    >
  
    <ng-template matStepperIcon="edit" MatStepperIconContext>
      <mat-icon class="icon-override"></mat-icon>
    </ng-template>
  
    <ng-template matStepperIcon="done" MatStepperIconContext>
      <mat-icon class="icon-override"></mat-icon>
    </ng-template>
  
    <ng-template matStepperIcon="number" MatStepperIconContext>
      <mat-icon class="icon-override"></mat-icon>
    </ng-template>
  
    @for (item of items; track item) {
      <mat-step>
        @if (item?.data.type !== 'declined') {
          <ng-template matStepLabel>
            <tidy-text
              [body]="item?.text">
            </tidy-text>
          </ng-template>
        }
        @if (item?.data.type === 'declined') {
          <ng-template matStepLabel>
            <tidy-text
              [body]="'The pro declined'">
            </tidy-text>
          </ng-template>
          <tidy-row class="vertical-align-center step-content-row">
            <tidy-image
              class="step-icon force-width"
              [src]="'assets/svg/information-circle-outline.svg'">
            </tidy-image>
            <tidy-text
              [body]="'Declined Reason:' + builDeclinedString(item?.text)">
            </tidy-text>
          </tidy-row>
        }
        @if (item?.data?.show_at) {
          <tidy-row class="vertical-align-center step-content-row">
            <tidy-image
              class="step-icon force-width"
              [src]="'assets/svg/calendar-outline.svg'">
            </tidy-image>
            <tidy-text
              [body]="buildDate(item?.data?.show_at)">
            </tidy-text>
          </tidy-row>
        }
        @if (item?.data?.homekeeper_id) {
          <tidy-row class="vertical-align-center step-content-row">
            <tidy-image
              class="step-icon force-width"
              [src]="'assets/svg/person-circle-outline.svg'">
            </tidy-image>
            <tidy-text
              class="link"
              [body]="'View Pro'"
              (action)="viewPro(item?.data?.homekeeper_id)">
            </tidy-text>
          </tidy-row>
        }
        @if (notificationHistory?.notifications) {
          @if (notificationHistory?.notifications?.app) {
            <tidy-row class="vertical-align-center step-content-row">
              <tidy-image
                class="step-icon force-width"
                [src]="notificationHistory?.notifications?.app?.status === 'sent' ? 'assets/svg/checkmark-circle-outline.svg' : 'assets/svg/alert-circle-outline.svg'">
              </tidy-image>
              <tidy-text
                [body]="notificationHistory?.notifications?.app?.status === 'sent' ? 'Notified in app' : 'In-app notifications not set up'">
              </tidy-text>
              @if (notificationHistory?.notifications?.app?.status === 'sent') {
                <tidy-text
                  class="link"
                  [body]="' Details'"
                  (action)="viewHistory(notificationHistory, 'app')">
                </tidy-text>
              }
            </tidy-row>
          }
          @if (notificationHistory?.notifications?.sms) {
            <tidy-row class="vertical-align-center step-content-row">
              <tidy-image
                class="step-icon force-width"
                [src]="notificationHistory?.notifications?.sms?.status === 'queued' ? 'assets/svg/checkmark-circle-outline.svg' : 'assets/svg/alert-circle-outline.svg'">
              </tidy-image>
              <tidy-text
                [body]="notificationHistory?.notifications?.sms?.status === 'queued' ? 'Notified via sms' : 'Failed to notify via sms'">
              </tidy-text>
              @if (notificationHistory?.notifications?.sms?.status === 'queued') {
                <tidy-text
                  class="link"
                  [body]="' Details'"
                  (action)="viewHistory(notificationHistory, 'sms')">
                </tidy-text>
              }
            </tidy-row>
          }
          @if (notificationHistory?.notifications?.email) {
            <tidy-row class="vertical-align-center step-content-row">
              <tidy-image
                class="step-icon force-width"
                [src]="notificationHistory?.notifications?.email?.status === 'OK' ? 'assets/svg/checkmark-circle-outline.svg' : 'assets/svg/alert-circle-outline.svg'">
              </tidy-image>
              <tidy-text
                [body]="notificationHistory?.notifications?.email?.status === 'OK' ? 'Notified via email' : 'Failed to notify via email'">
              </tidy-text>
              @if (notificationHistory?.notifications?.email?.status === 'OK') {
                <tidy-text
                  class="link"
                  [body]="' Details'"
                  (action)="viewHistory(notificationHistory, 'email')">
                </tidy-text>
              }
            </tidy-row>
          }
        }
      </mat-step>
    }
  
  </mat-stepper>
  `,
    standalone: false
})

export class StepperComponent implements AfterViewInit {

  @Input() items: any;
  @ViewChild('stepper') stepper: MatStepper;
  @Output() selectionChange = new EventEmitter<any>();
  @Output() viewProAction = new EventEmitter<string>();
  @Output() viewHistoryDetails = new EventEmitter<any>();
  @Input() notificationHistory: any;
  stepsRefs: HTMLElement[];

  readonly cdklayerLimit = 30;

  constructor(
    private elementRef: ElementRef
  ) {

  }

  ngAfterViewInit() {
    const tidyStepperRef = this.elementRef.nativeElement as HTMLElement;

    const matStepperRef = tidyStepperRef.firstChild as HTMLElement;

    this.stepsRefs = Array.from(matStepperRef.children) as HTMLElement[];

    this.collapseAll(this.stepsRefs);
    this.updateIcons(this.stepsRefs);
  }

  collapseAll(matSteps: HTMLElement[], collapse = true) {
    matSteps.forEach(step => {
      const stepContent = step.children.item(1).children.item(0);

      collapse ?
        stepContent.classList.add('collapsed') :
        stepContent.classList.remove('collapsed');
    });
  }

  updateIcons(matSteps: HTMLElement[]) {

    matSteps.forEach((step, index) => {
      const stepLabel = step.firstChild as HTMLElement;

      const addingClass = this.items[index].data.type ?? 'info';
      stepLabel.classList.add(addingClass);
    });
  }

  buildDate(date) {
    const parseDateTimePipe = new ParseDateTimePipe();
    const zonedDate = LuxonDateTime.fromISO(date).setZone(LuxonDateTime.local().zoneName).toISO();
    return `${parseDateTimePipe.transform(zonedDate, 'ddd MMM d')  } ${  parseDateTimePipe.transform(zonedDate, 'h:mma')}`;
  }

  builDeclinedString(text) {
    let newText = '';
    newText = text.replace("The pro declined,", "");
    newText = newText.replace("saying", "said");
    return newText;
  }

  viewPro(proId) {
    this.viewProAction.emit(proId);
  }

  viewHistory(history, type) {
    history.notification_type = type;
    this.viewHistoryDetails.emit(history);
  }

  stepChange(selectionChange: StepperSelectionEvent) {
    this.selectionChange.emit(selectionChange);
    this.focusUnfocusStep(selectionChange.selectedIndex);
    this.focusUnfocusStep(selectionChange.previouslySelectedIndex, false);
  }

  focusUnfocusStep(index, focus = true) {
    const stepLabel = this.stepsRefs[index].firstChild as HTMLElement;

    if (stepLabel) {
      stepLabel.classList[focus ? 'add' : 'remove']('focused-step');
    } else {
      this.focusUnfocusStep(index, focus);
    }
  }
}
